import { Button } from '@repo/ui/button'
import { Checkbox } from '@repo/ui/checkbox'
import { CheckCheck, SquareStack } from 'lucide-react'
import { useEffect, useState } from 'react'

export type MultiSelectItem = {
  id: string
  label: string
}

export function MultiSelect({
  className,
  initialSelectedIds = [],
  items,
  onSelection,
  canUnselectAll = true,
}: {
  className?: string
  initialSelectedIds?: string[]
  items: MultiSelectItem[]
  onSelection: (selectedItemIds: string[]) => void
  canUnselectAll?: boolean
}) {
  const [selectedItems, setSelectedItems] = useState<string[]>(initialSelectedIds)

  useEffect(() => {
    onSelection(selectedItems)
  }, [selectedItems])
  const handleSelect = (id: string) => {
    const isSelected = selectedItems.includes(id)

    if (isSelected && selectedItems.length === 1 && !canUnselectAll) {
      // Do not allow deselecting if there's only one item selected and unselecting all is not allowed
      return
    }

    setSelectedItems((prev) => {
      if (isSelected) {
        // Deselect item
        return prev.filter((itemId) => itemId !== id)
      } else {
        // Select item
        return [...prev, id]
      }
    })
  }

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedItems([])
    else setSelectedItems(items.map((item) => item.id))
  }

  const isAllSelected = selectedItems.length === items.length
  return (
    <div className={className}>
      <div className="grid gap-2">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-2">
            <Checkbox
              checked={selectedItems.includes(item.id)}
              onCheckedChange={() => handleSelect(item.id)}
              id={item.id}
            />
            <label htmlFor={item.id} className="text-primary-600 cursor-pointer select-none">
              {item.label}
            </label>
          </div>
        ))}
      </div>
      {items.length > 3 && (
        <div className="mt-4 flex items-center">
          <Button onClick={toggleSelectAll} type="button" variant="outline" size="sm">
            {isAllSelected ? (
              <>
                <SquareStack className="size-4" /> Deselect All
              </>
            ) : (
              <>
                <CheckCheck className="size-4" /> Select All
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
